import React, { useEffect, useState } from 'react';
import { usePortalStore } from '../../store';
import { Users, MessageSquare, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import type { BoardPersona } from '../../types';

const SCOPE_COLORS: Record<string, string> = {
  constitutional: 'bg-purple-900/40 text-purple-300 border-purple-700',
  strategic: 'bg-blue-900/40 text-blue-300 border-blue-700',
  operational: 'bg-cyan-900/40 text-cyan-300 border-cyan-700',
  technical: 'bg-emerald-900/40 text-emerald-300 border-emerald-700',
  governance: 'bg-amber-900/40 text-amber-300 border-amber-700',
};

const PersonaCard: React.FC<{ persona: BoardPersona }> = ({ persona }) => {
  const [expanded, setExpanded] = useState(false);

  if (persona.error) {
    return (
      <div className="border border-red-800 bg-red-950/30 rounded-xl p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-red-300">{persona.id}</p>
          <p className="text-xs text-red-400 mt-0.5">{persona.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-slate-700 bg-slate-900 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-cyan-400 flex-shrink-0" />
              <h3 className="text-base font-semibold text-slate-100 truncate">{persona.name}</h3>
            </div>
            <p className="text-xs text-cyan-500 font-medium uppercase tracking-wider mb-3">{persona.archetype}</p>

            {/* Domain tags */}
            <div className="flex flex-wrap gap-1.5 mb-3">
              {persona.domain.map(d => (
                <span key={d} className="px-2 py-0.5 bg-slate-800 text-slate-300 text-xs rounded-full border border-slate-600">
                  {d}
                </span>
              ))}
            </div>

            {/* Signature question */}
            <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 mb-3">
              <p className="text-xs font-medium text-slate-400 mb-1 flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" />
                Signature question
              </p>
              <p className="text-sm text-slate-200 italic">&ldquo;{persona.signature_question}&rdquo;</p>
            </div>

            {/* Known bias */}
            <div className="flex items-start gap-2 mb-3">
              <AlertCircle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-300">
                <span className="font-medium">Known bias: </span>{persona.known_bias}
              </p>
            </div>

            {/* Advisory scope */}
            <div className="flex flex-wrap gap-1.5">
              {persona.advisory_scope.map(s => (
                <span
                  key={s}
                  className={`px-2 py-0.5 text-xs rounded border font-medium ${SCOPE_COLORS[s] ?? 'bg-slate-800 text-slate-300 border-slate-600'}`}
                >
                  {s}
                </span>
              ))}
            </div>
          </div>

          {/* Read-only badge */}
          <div className="flex-shrink-0 flex flex-col items-end gap-2">
            <span className="px-2 py-1 bg-slate-800 border border-slate-600 text-slate-400 text-xs rounded font-medium">
              advisory only
            </span>
            <span className="px-2 py-1 bg-red-950/40 border border-red-800 text-red-400 text-xs rounded font-medium">
              write: none
            </span>
          </div>
        </div>

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors"
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {expanded ? 'Hide advisory profile' : 'Show advisory profile'}
        </button>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="border-t border-slate-700 px-5 py-4 bg-slate-950/40">
          <pre className="text-xs text-slate-300 whitespace-pre-wrap font-sans leading-relaxed">
            {persona.body}
          </pre>
          <div className="mt-3 pt-3 border-t border-slate-800 flex items-center justify-between">
            <span className="text-xs text-slate-500">
              Lens tag: <code className="text-cyan-500">@{persona.lens_tag}</code>
            </span>
            <span className="text-xs text-slate-500">Last active: {persona.last_active}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export const BoardView: React.FC = () => {
  const { boardPersonas, loadBoardPersonas } = usePortalStore();

  useEffect(() => {
    loadBoardPersonas();
  }, []);

  const validPersonas = boardPersonas.filter(p => !p.error);
  const errorPersonas = boardPersonas.filter(p => p.error);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Users className="w-6 h-6 text-cyan-400" />
          <h1 className="text-2xl font-bold text-slate-100">AI Board — Advisory Personas</h1>
        </div>
        <p className="text-sm text-slate-400 max-w-2xl">
          Non-executing advisor personas. Each provides a distinct decision lens that Lucas
          cites in Discord replies. No persona has execution rights, write access, or Layer 2
          dispatch capability.
        </p>
        <div className="mt-3 flex items-center gap-3">
          <span className="px-3 py-1.5 bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-lg font-medium">
            {validPersonas.length} active advisor{validPersonas.length !== 1 ? 's' : ''}
          </span>
          <span className="px-3 py-1.5 bg-red-950/30 border border-red-800 text-red-300 text-xs rounded-lg font-medium">
            execution controls: none
          </span>
          <span className="px-3 py-1.5 bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-lg font-medium">
            read-only view
          </span>
        </div>
      </div>

      {/* Personas grid */}
      {boardPersonas.length === 0 ? (
        <div className="border border-slate-700 bg-slate-900 rounded-xl p-12 text-center">
          <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No personas loaded</p>
          <p className="text-sm text-slate-500 mt-1">
            Add persona files to CONSTITUTION/BOARD/personas/
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {validPersonas.map(persona => (
            <PersonaCard key={persona.id} persona={persona} />
          ))}
          {errorPersonas.map(persona => (
            <PersonaCard key={persona.id} persona={persona} />
          ))}
        </div>
      )}

      {/* Footer note */}
      <div className="mt-8 p-4 bg-slate-900 border border-slate-700 rounded-xl">
        <p className="text-xs text-slate-400 leading-relaxed">
          <strong className="text-slate-300">Governance note:</strong> Advisor personas are defined
          in <code className="text-cyan-500">CONSTITUTION/BOARD/personas/</code> and require board
          approval to add, modify, or remove. Schema is versioned in{' '}
          <code className="text-cyan-500">CONSTITUTION/BOARD/persona-schema.md</code>.
          Personas are rhetorical lenses for Lucas — they do not hold API credentials, tool access,
          or Petri net transitions.
        </p>
      </div>
    </div>
  );
};
